import Card from '@/components/card';
import CollapsibleTable, {
  CellComponentProps
} from '@/components/collapsible-table';
import { EMPTY_FIELD_PLACEHOLDER } from '@/consts/formatting';
import { DeconversionDestinationStatus } from '@/models/PlanDeconversionDTO.model';
import { PlanService } from '@/services/Plan.service';
import formatters from '@/utils/Formatters';
import { Search } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TableCell,
  TextField,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { FC, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { useDebounce } from 'use-debounce';

import { DeconversionDestinationDrawer } from './DeconversionDestinationDrawer';

const columns = [
  { field: 'name', headerName: 'Destination Name' },
  { field: 'accountNumber', headerName: 'Account Number' },
  { field: 'status', headerName: 'Status' },
  { field: 'updatedAt', headerName: 'Last Updated' },
  { field: 'action', headerName: 'Details' }
];

const DeconversionDestinationsCell: FC<CellComponentProps> = (
  props: CellComponentProps
) => {
  const [open, toggleOpen] = useToggle(false);
  let field = props.row[props.column.field];

  if (props.column.field === 'accountNumber') {
    field = (
      <Typography variant='body1'>
        {props.row.bankInfo?.accountNumber}
      </Typography>
    );
  }

  if (props.column.field === 'updatedAt') {
    field = (
      <Typography variant='body1'>
        {formatters.formatFromIsoDateCustom(
          props.row[props.column.field],
          'M/DD/YY HH:mm'
        )}
      </Typography>
    );
  }

  if (props.column.field === 'action') {
    field = (
      <IconButton aria-label='details' onClick={toggleOpen}>
        <ChevronRight />
      </IconButton>
    );
  }

  if (props.column.field === 'status') {
    field =
      props.row[props.column.field] === DeconversionDestinationStatus.Active ? (
        <Stack alignItems='center' direction='row' spacing={1}>
          <CheckIcon color='success' fontSize='small' />
          <Typography variant='body2'>Active</Typography>
        </Stack>
      ) : props.row[props.column.field] ===
        DeconversionDestinationStatus.PendingApproval ? (
        <Stack alignItems='center' direction='row' spacing={1}>
          <PendingOutlinedIcon color='warning' fontSize='small' />
          <Typography variant='body2'>Pending Approval</Typography>
        </Stack>
      ) : (
        <Typography variant='body2'>{EMPTY_FIELD_PLACEHOLDER}</Typography>
      );
  }

  return (
    <>
      <TableCell component='th' scope='row'>
        <Box>
          {!field ? <Typography variant='body1'>N/A</Typography> : field}
        </Box>
      </TableCell>
      <DeconversionDestinationDrawer
        data={props.row}
        onClose={toggleOpen}
        open={open}
      />
    </>
  );
};

export const DeconversionRoute: FC = () => {
  const [open, toggleOpen] = useToggle(false);
  const [searchTerm, setSearchTerm] = useState('');

  const deconversionDestinationsQuery = useQuery(
    ['PlanService.deconversionDestinations'],
    () => {
      return PlanService.getDeconversionDestinations(1);
    }
  );

  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const tableData = useMemo(() => {
    if (!deconversionDestinationsQuery.data?.data) {
      return [];
    }

    return deconversionDestinationsQuery.data.data.filter(destination => {
      const name = destination.name || '';
      return name.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    });
  }, [deconversionDestinationsQuery.data?.data, debouncedSearchTerm]);

  const searchProgramPlans = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setSearchTerm(search);
  };

  return (
    <>
      <Typography mb={4} variant='h4'>
        Deconversion
      </Typography>
      <Card>
        <Stack>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            p={2}>
            <Stack direction='row' spacing={2}>
              <Typography variant='h6'>Destination List</Typography>
              <TextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Search />
                    </InputAdornment>
                  ),
                  onChange: searchProgramPlans
                }}
                label='Search by name'
                size='small'
                value={searchTerm}
                variant='outlined'
              />
            </Stack>
            <Button
              onClick={toggleOpen}
              startIcon={<AddIcon />}
              variant='outlined'>
              ADD
            </Button>
          </Stack>
        </Stack>
        <CollapsibleTable
          backgroundPaperElevation={0}
          cellComponent={DeconversionDestinationsCell}
          columns={columns}
          tableData={tableData || []}
        />
      </Card>
      <DeconversionDestinationDrawer onClose={toggleOpen} open={open} />
    </>
  );
};

DeconversionRoute.displayName = 'DeconversionRoute';
